import React, { useState, useEffect } from "react";
import { Spin, message, Popconfirm, Input, Button, Modal, Tabs } from "antd";
import {
  MailOutlined, PhoneOutlined,
  InboxOutlined, CheckCircleFilled, CheckCircleOutlined,
  UserOutlined,
} from "@ant-design/icons";
import drEasyApi from "../../../Api/Customapi";
import { formatDateTime } from "../../../utils/helpers";

const BRAND = "#298d9f";
const BRAND_LIGHT = "#e8f4f7";
  const role=localStorage.getItem("role");

const ContactScreen = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showRead, setShowRead] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [isEmailModalVisible, setIsEmailModalVisible] = useState(false);
  const [newContactEmail, setNewContactEmail] = useState("");

  useEffect(() => { fetchContacts(); }, [showRead]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const res = await drEasyApi.get("/api/Contact/GetContacts", {
        params: { PageNumber: 1, PageSize: 50, Deleted: showRead },
      });
      setContacts(res.data.contacts || []);
      setTotalCount(res.data?.data?.totalCount ?? res.data?.totalCount ?? 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleRead = async (id) => {
    try {
      await drEasyApi.put("/api/Contact/ToogleDeleted", { id });
      message.success(showRead ? "Marked as unread" : "Marked as read");
      setSelectedContact(null);
      fetchContacts();
    } catch (err) {
      message.error(err?.response?.data?.errors?.[0] || "Failed to update");
    }
  };

  const handleChangeContactEmail = async () => {
    if (!newContactEmail || !newContactEmail.includes("@")) {
      message.error("Please enter a valid email address");
      return;
    }
    try {
      await drEasyApi.put("/api/Contact/ChangeContactMail", { newContactMail: newContactEmail });
      message.success("Contact email updated successfully");
      setIsEmailModalVisible(false);
      setNewContactEmail("");
    } catch (err) {
      message.error(err?.response?.data?.errors?.[0] || "Failed to update");
    }
  };

  const formatFullDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleString("en-US", {
      month: "long", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  };

  const getInitials = (name) =>
    name?.split(" ").map((n) => n[0]).join("").substring(0, 2).toUpperCase() || "?";

  const avatarColors = [BRAND, "#2a9d8f", "#e76f51", "#f4a261", "#457b9d", "#6d6875"];
  const getAvatarColor = (name) => avatarColors[(name?.charCodeAt(0) || 0) % avatarColors.length];

  const filtered = contacts.filter((c) =>
    [c.name, c.email, c.subject].some((f) => f?.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Called as a function (not <Component />) so the list doesn't remount on every render
  const renderMessageList = () =>
    loading ? (
      <div className="flex justify-center pt-12"><Spin size="large" /></div>
    ) : filtered.length === 0 ? (
      <div className="text-center pt-16 text-gray-400">
        <InboxOutlined style={{ fontSize: 52, display: "block", marginBottom: 12 }} />
        <p className="m-0 text-[15px]">{showRead ? "No read messages" : "No unread messages"}</p>
      </div>
    ) : (
      <div className="flex flex-col gap-2">
        {filtered.map((contact) => (
          <div
            key={contact.id}
            onClick={() => setSelectedContact(contact)}
            className="flex items-center gap-3 sm:gap-3.5 bg-white rounded-xl p-3 sm:px-4 sm:py-3.5 cursor-pointer border border-gray-200 transition-all shadow-sm active:scale-[0.99] md:hover:-translate-y-px"
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = `0 4px 16px ${BRAND}22`;
              e.currentTarget.style.borderColor = BRAND;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = "";
              e.currentTarget.style.borderColor = "";
            }}
          >
            {/* Avatar */}
            <div
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full shrink-0 flex items-center justify-center text-sm sm:text-[17px] font-bold text-white"
              style={{ background: getAvatarColor(contact.name) }}
            >
              {getInitials(contact.name)}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex justify-between items-start gap-2">
                <span className="font-bold text-sm text-gray-900 truncate min-w-0">{contact.name}</span>
                <span
                  className="text-[11px] shrink-0 whitespace-nowrap"
                  style={{ color: !showRead ? BRAND : "#9ca3af" }}
                >
                  {formatDateTime(contact.createdAt)}
                </span>
              </div>

              <div className="flex justify-between items-center mt-0.5 gap-2">
                <div className="min-w-0 flex-1 truncate">
                  <span className="text-xs font-semibold" style={{ color: BRAND }}>{contact.subject}</span>
                  {contact.note && (
                    <span className="text-xs text-gray-400 ml-1.5">
                      — {contact.note.substring(0, 50)}{contact.note.length > 50 ? "..." : ""}
                    </span>
                  )}
                </div>
                {!showRead && (
                  <div
                    className="w-5 h-5 rounded-full text-white text-[10px] font-bold shrink-0 flex items-center justify-center"
                    style={{ background: BRAND }}
                  >1</div>
                )}
              </div>

              <div className="mt-1 text-[11px] text-gray-400 flex flex-wrap gap-x-3 gap-y-0.5 min-w-0">
                <span className="truncate max-w-full">
                  <MailOutlined className="mr-1" />{contact.email}
                </span>
                {contact.phone && (
                  <span className="whitespace-nowrap">
                    <PhoneOutlined className="mr-1" />{contact.phone}
                  </span>
                )}
              </div>
            </div>

            {/* Read indicator */}
            {showRead && (
              <CheckCircleFilled className="shrink-0" style={{ color: BRAND, fontSize: 18 }} />
            )}
          </div>
        ))}
      </div>
    );

  const renderTabLabel = (icon, text, active) => (
    <span className="px-1 sm:px-4 py-1 sm:py-2 font-semibold text-sm sm:text-base whitespace-nowrap">
      {icon}
      {text}
      {filtered.length > 0 && active && (
        <span
          className="ml-2 text-white rounded-[10px] px-2 py-px text-[11px] font-bold align-middle"
          style={{ background: BRAND }}
        >
          {contacts.length}
        </span>
      )}
    </span>
  );

  return (
    <div className="p-0 sm:p-4 md:p-6 overflow-x-hidden min-h-screen">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4 sm:mb-5">
        <div>
          <h2 className="m-0 text-lg sm:text-xl font-bold text-gray-800">Contact Messages</h2>
          <span className="text-[13px] text-gray-500">
            {filtered.length} {showRead ? "read" : "unread"} message{totalCount !== 1 ? "s" : ""}
          </span>
        </div>
       {role!=7&& <Button
          icon={<MailOutlined />}
          onClick={() => setIsEmailModalVisible(true)}
          className="w-full sm:w-auto"
          style={{ background: BRAND, color: "#fff", border: "none", borderRadius: 8, fontWeight: 600 }}
        >
          Change Contact Email
        </Button>}
      </div>

      {/* ── Tabs ── */}
      <Tabs
        activeKey={showRead ? "read" : "unread"}
        onChange={(key) => setShowRead(key === "read")}
        size="large"
        tabBarStyle={{ marginBottom: 16, borderBottom: "2px solid #e8e8e8" }}
        items={[
          {
            key: "unread",
            label: renderTabLabel(<InboxOutlined className="mr-2" />, "Unread", !showRead),
            children: !showRead ? renderMessageList() : null,
          },
          {
            key: "read",
            label: renderTabLabel(<CheckCircleFilled className="mr-2" />, "Read", showRead),
            children: showRead ? renderMessageList() : null,
          },
        ]}
      />

      {/* ── Message details modal ── */}
      <Modal
        open={!!selectedContact}
        onCancel={() => setSelectedContact(null)}
        footer={null}
        centered
        width="min(600px, calc(100vw - 24px))"
        destroyOnClose
        styles={{
          header: { borderBottom: "none", paddingBottom: 0 },
          body: { padding: 0, maxHeight: "80vh", overflowY: "auto", overflowX: "hidden" },
        }}
      >
        {selectedContact && (
          <div>
            <div className="text-[13px]" style={{ color: "rgba(0,0,0,0.75)" }}>
              {formatFullDate(selectedContact.createdAt)}
            </div>

            <div className="py-4 sm:py-5 sm:px-6 flex flex-col gap-4">

              {/* Contact info */}
              <div
                className="rounded-[10px] p-3.5 flex flex-col gap-2 min-w-0"
                style={{ background: BRAND_LIGHT, borderLeft: `3px solid ${BRAND}` }}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <UserOutlined style={{ color: BRAND, fontSize: 14 }} />
                  <span className="text-sm font-semibold text-gray-700 break-words min-w-0">{selectedContact.name}</span>
                </div>
                <div className="flex items-center gap-2 min-w-0">
                  <MailOutlined style={{ color: BRAND, fontSize: 14 }} />
                  <a href={`mailto:${selectedContact.email}`} className="text-[13px] break-all min-w-0" style={{ color: BRAND }}>
                    {selectedContact.email}
                  </a>
                </div>
                {selectedContact.phone && (
                  <div className="flex items-center gap-2">
                    <PhoneOutlined style={{ color: BRAND, fontSize: 14 }} />
                    <a href={`tel:${selectedContact.phone}`} className="text-[13px]" style={{ color: BRAND }}>
                      {selectedContact.phone}
                    </a>
                  </div>
                )}
              </div>

              {/* Subject */}
              <div>
                <div className="text-[11px] text-gray-400 font-semibold uppercase tracking-wide mb-1.5">
                  Subject
                </div>
                <div className="bg-gray-50 rounded-lg px-3.5 py-2.5 text-sm font-semibold text-gray-800 border border-gray-200 break-words">
                  {selectedContact.subject}
                </div>
              </div>

              {/* Message */}
              {selectedContact.note && (
                <div>
                  <div className="text-[11px] text-gray-400 font-semibold uppercase tracking-wide mb-1.5">
                    Message
                  </div>
                  <div
                    className="bg-gray-50 rounded-lg px-3.5 py-3 text-sm text-gray-700 border border-gray-200 break-words overflow-y-auto max-h-[220px]"
                    style={{ lineHeight: 1.7, whiteSpace: "pre-wrap" }}
                  >
                    {selectedContact.note}
                  </div>
                </div>
              )}

              {/* Footer actions */}
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 pt-1">
                <a
                  href={`mailto:${selectedContact.email}?subject=Re: ${encodeURIComponent(selectedContact.subject || "")}`}
                  className="flex items-center justify-center gap-1.5 text-white rounded-lg px-[18px] py-2 font-semibold text-[13px] no-underline"
                  style={{ background: BRAND }}
                >
                  <MailOutlined /> Reply via Email
                </a>

                <Popconfirm
                  title={showRead ? "Mark as unread?" : "Mark as read?"}
                  onConfirm={() => handleToggleRead(selectedContact.id)}
                  overlayStyle={{ maxWidth: "calc(100vw - 24px)" }}
                >
                  <button
                    className="flex items-center justify-center gap-1.5 rounded-lg px-[18px] py-2 cursor-pointer text-[13px] font-semibold"
                    style={{
                      background: showRead ? "#fff" : BRAND_LIGHT,
                      color: showRead ? "#6b7280" : BRAND,
                      border: `1.5px solid ${showRead ? "#d1d5db" : BRAND}`,
                    }}
                  >
                    {showRead ? <CheckCircleOutlined /> : <CheckCircleFilled style={{ color: BRAND }} />}
                    {showRead ? "Mark as Unread" : "Mark as Read"}
                  </button>
                </Popconfirm>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Change email modal ── */}
      <Modal
        title="Change Contact Email"
        open={isEmailModalVisible}
        onCancel={() => { setIsEmailModalVisible(false); setNewContactEmail(""); }}
        centered
        width="min(520px, calc(100vw - 24px))"
        footer={[
          <Button key="cancel" onClick={() => { setIsEmailModalVisible(false); setNewContactEmail(""); }}>Cancel</Button>,
          <Button key="save" type="primary" onClick={handleChangeContactEmail}
            style={{ backgroundColor: BRAND, borderColor: BRAND }}>Update Email</Button>,
        ]}
        destroyOnClose
      >
        <div className="py-3">
          <p className="text-gray-500 text-sm mb-3">
            Update the email where contact form submissions will be sent.
          </p>
          <Input
            prefix={<MailOutlined style={{ color: "#9ca3af" }} />}
            type="email" placeholder="contact@example.com"
            value={newContactEmail}
            onChange={(e) => setNewContactEmail(e.target.value)}
          />
        </div>
      </Modal>

      <style>{`
        ::-webkit-scrollbar { width: 5px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px; }
      `}</style>
    </div>
  );
};

export default ContactScreen;